import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

import { Spinner } from './spinner';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'kiosk';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-on-brand hover:bg-brand-hover active:bg-brand-active shadow-e1',
  secondary:
    'bg-surface-sunken text-fg hover:bg-surface-sunken/70 border border-border',
  outline: 'border border-border-strong text-fg hover:bg-surface-sunken',
  ghost: 'text-fg hover:bg-surface-sunken',
  destructive: 'bg-danger text-white hover:brightness-110 active:brightness-95 shadow-e1',
  link: 'text-brand underline-offset-4 hover:underline',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm rounded-md gap-1.5',
  md: 'h-10 px-4 text-body rounded-md gap-2',
  lg: 'h-12 px-6 text-card-title rounded-lg gap-2',
  icon: 'h-10 w-10 rounded-md',
  // The kiosk is operated at arm's length by someone holding a phone in the other
  // hand, so its targets are far past the 44px minimum.
  kiosk: 'h-20 px-10 text-kiosk-body rounded-xl gap-4',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button and shows a spinner without the button changing width. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Renders as the single child element, for links that should look like buttons. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leadingIcon,
      trailingIcon,
      asChild = false,
      className,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const classes = cn(
      'relative inline-flex shrink-0 cursor-pointer items-center justify-center font-medium whitespace-nowrap',
      'transition-colors duration-150',
      'disabled:pointer-events-none disabled:opacity-50',
      VARIANTS[variant],
      SIZES[size],
      className,
    );

    // Slot requires exactly one React element child. The label span and loading
    // overlay would make that impossible, so asChild takes a separate path.
    if (asChild) {
      return (
        <Slot.Root
          ref={ref}
          aria-busy={loading || undefined}
          className={classes}
          {...props}
        >
          {children}
        </Slot.Root>
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        className={classes}
        {...props}
      >
        {/* The label keeps its space while loading; a button that shrinks to a
            spinner moves everything beside it and makes a slow save feel broken. */}
        <span
          className={cn(
            'inline-flex items-center gap-[inherit]',
            loading && 'invisible',
          )}
        >
          {leadingIcon}
          {children}
          {trailingIcon}
        </span>

        {loading ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner className="h-4 w-4" />
          </span>
        ) : null}
      </button>
    );
  },
);

Button.displayName = 'Button';
